import { useSchoolContext } from '@/App/hooks'
import { useCurrentReflection, useCurrentSchoolId } from '@/hooks'
import { isTokenDying } from '@/sessions'
import { userAgent } from '@seiue/ua'
import { Toast } from '@seiue/ui'
import { isNaN, useOnMount } from '@seiue/util'
import { Spin } from 'antd'
import { authApi, UserDomainEnum } from 'packages/sdks-next/growp'
import { getUrlParams } from 'packages/utils'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { MainContent } from './MainContent'

/**
 * 教师界面登录
 *
 * @param param - 参数
 * @param param.isPreview - 是否是预览
 * @returns login page
 */
export const AdminLogin: React.FC<{
  isPreview?: boolean
}> = ({ isPreview }) => {
  const regex = /\/login\/(.*)/
  const result = location.pathname.match(regex)
  const urlSchoolIdRaw = result?.[1]

  const [loading, setLoading] = useState<boolean>(true)

  // 处理 URL 编码的情况，提取纯数字部分
  let urlSchoolId: string | undefined
  if (urlSchoolIdRaw) {
    try {
      const decoded = decodeURIComponent(urlSchoolIdRaw)
      const match = decoded.match(/^(\d+)/)
      urlSchoolId = match?.[1]
    } catch {
      const match = urlSchoolIdRaw.match(/^(\d+)/)
      urlSchoolId = match?.[1]
    }
  }

  useOnMount(() => {
    if (!isPreview) {
      if (!urlSchoolId || isNaN(Number(urlSchoolId))) {
        if (urlSchoolIdRaw?.includes('/')) {
          Toast.warning('路由错误')
        } else {
          Toast.warning('未找到学校信息，请确认登录链接是否正确')
        }

        location.href = '/404'
      }
    }
  })

  const data = useSchoolContext()
  const schoolId = useCurrentSchoolId()

  const reflection = useCurrentReflection()

  const isOidc = !!getUrlParams('client_id')
  const openLogin = {
    clientId: getUrlParams('client_id'),
    scope: getUrlParams('scope'),
    state: getUrlParams('state'),
    redirectUri: getUrlParams('redirect_uri'),
    responseType: getUrlParams('response_type'),
    nonce: getUrlParams('nonce'),
  }

  useEffect(() => {
    if (
      reflection &&
      !isTokenDying(reflection.expiredAt) &&
      schoolId &&
      isOidc
    ) {
      // 如果是来自 oidc 请求并且当前用户已登陆，静默触发 oidc 流程
      const postData = {
        openLogin,
        schoolId: Number(schoolId),
        account: '',
        password: '',
      }

      authApi
        .login(UserDomainEnum.Formal, postData)
        .then((response) => {
          if (response.data.redirectUri) {
            location.href = response.data.redirectUri
            return
          }
        })
        .catch((e) => {
          setLoading(false)
          Toast.error(e.response?.data?.message || '登录失败')
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false)
          }, 1000)
        })
    } else {
      setLoading(false)
    }
  }, [reflection, schoolId, isOidc])

  if ((!isPreview && isNaN(Number(urlSchoolId))) || loading)
    return <StyledSpin />

  return (
    <Wrapper>
      {userAgent.isMobile ? null : (
        <ImageWrapper
          style={{
            backgroundImage: `url(${data?.bgImage || '/login-bg.png'})`,
          }}
        >
          <Mask>
            {schoolId === 3 ? <OuterImg src={'/bdfz-icon.png'} /> : null}
          </Mask>
        </ImageWrapper>
      )}

      <ContentWrapper>
        <MainContent
          schoolConfig={data}
          isPreview={isPreview}
        />
      </ContentWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f6f6f6;

  @media (min-width: 768px) {
    flex-direction: row;
    overflow: auto;
  }

  ${userAgent.isMobile &&
  css`
    flex-direction: column;
    padding: 16px;
    overflow: auto;
  `}

  @media (max-width: 767px) {
    flex-direction: column;
    padding: 16px;
    overflow: auto;
  }
`

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;

  ${userAgent.isMobile &&
  css`
    padding: 16px;
  `}

  @media (max-width: 767px) {
    padding: 16px;
  }
`

const ImageWrapper = styled.div`
  height: 100vh;
  width: 640px;
  overflow: hidden;
  background-size: cover;
  min-height: 580px;

  ${userAgent.isMobile &&
  css`
    display: none;
  `}

  @media (max-width: 767px) {
    display: none;
  }
`

const Mask = styled.div`
  background-color: rgba(0, 0, 0, 0.12);
  width: 100%;
  height: 100%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const OuterImg = styled.img`
  width: 164px;
`

const StyledSpin = styled(Spin)`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`
